import type { CallDirection, CallStatus, ID } from './types.js';

export type ReportKind =
  | 'overview'
  | 'call_history'
  | 'call_recordings'
  | 'monthly_call_volume'
  | 'user_statistics'
  | 'queue_statistics'
  | 'ring_group_statistics';

export type ReportFormat = 'csv' | 'pdf';
export type ReportDirectionTab = 'total' | 'inbound' | 'outbound';

export interface ReportDateRange {
  from: string;
  to: string;
}

export interface ReportTimeRange {
  from: string;
  to: string;
}

export interface ReportFilters {
  dateRange: ReportDateRange;
  timeRange?: ReportTimeRange;
  agentId?: ID;
  campaignId?: ID;
  queueId?: ID;
  ringGroupId?: ID;
  twilioNumberId?: ID;
  direction?: CallDirection;
  status?: CallStatus;
  disposition?: string;
  hasRecording?: boolean;
  phone?: string;
  /** Talk-time window in seconds: min is inclusive, max exclusive. */
  minDuration?: number;
  maxDuration?: number;
}

export interface TalkTimeBucket {
  key: TalkTimeBucketKey;
  /** Band label — the calls that fall in this bucket alone, e.g. "1m – 3m". */
  label: string;
  /** Cumulative label — this bucket and everything longer, e.g. "1m+". */
  atLeastLabel: string;
  /** Inclusive lower bound in seconds. */
  minSeconds: number;
  /** Exclusive upper bound in seconds; null means unbounded. */
  maxSeconds: number | null;
}

// "Answered" on its own lumps a three-second connect in with a three-minute
// conversation. These bands split answered calls by how long the parties
// actually talked, so a real conversation rate is visible next to the raw
// answer rate. Bands are disjoint and sum to `answered`; the cumulative
// "20s+ / 1m+ / 3m+" view is derived with `talkTimeAtLeast`.
export const TALK_TIME_BUCKETS = [
  { key: 'under20s', label: 'Under 20s', atLeastLabel: 'Any', minSeconds: 0, maxSeconds: 20 },
  { key: 'from20sTo1m', label: '20s – 1m', atLeastLabel: '20s+', minSeconds: 20, maxSeconds: 60 },
  { key: 'from1mTo2m', label: '1m – 2m', atLeastLabel: '1m+', minSeconds: 60, maxSeconds: 120 },
  { key: 'from2mTo3m', label: '2m – 3m', atLeastLabel: '2m+', minSeconds: 120, maxSeconds: 180 },
  { key: 'from3mTo5m', label: '3m – 5m', atLeastLabel: '3m+', minSeconds: 180, maxSeconds: 300 },
  { key: 'from5mTo10m', label: '5m – 10m', atLeastLabel: '5m+', minSeconds: 300, maxSeconds: 600 },
  { key: 'over10m', label: '10m+', atLeastLabel: '10m+', minSeconds: 600, maxSeconds: null },
] as const satisfies readonly TalkTimeBucket[];

export type TalkTimeBucketKey =
  | 'under20s'
  | 'from20sTo1m'
  | 'from1mTo2m'
  | 'from2mTo3m'
  | 'from3mTo5m'
  | 'from5mTo10m'
  | 'over10m';

/** Answered calls counted per talk-time band. Bands are disjoint. */
export type AnsweredTalkTime = Record<TalkTimeBucketKey, number>;

export function emptyTalkTime(): AnsweredTalkTime {
  return {
    under20s: 0,
    from20sTo1m: 0,
    from1mTo2m: 0,
    from2mTo3m: 0,
    from3mTo5m: 0,
    from5mTo10m: 0,
    over10m: 0,
  };
}

export function talkTimeBucketKey(seconds: number): TalkTimeBucketKey {
  const value = Math.max(0, seconds);
  for (const bucket of TALK_TIME_BUCKETS) {
    if (value >= bucket.minSeconds && (bucket.maxSeconds === null || value < bucket.maxSeconds)) {
      return bucket.key;
    }
  }
  return 'over10m';
}

/** Count of answered calls that talked for at least this bucket's lower bound. */
export function talkTimeAtLeast(buckets: AnsweredTalkTime, key: TalkTimeBucketKey): number {
  const from = TALK_TIME_BUCKETS.findIndex((b) => b.key === key);
  return TALK_TIME_BUCKETS.slice(from < 0 ? 0 : from).reduce((sum, b) => sum + buckets[b.key], 0);
}

export interface ReportSummary {
  totalCalls: number;
  inboundCalls: number;
  outboundCalls: number;
  answered: number;
  answeredByTalkTime: AnsweredTalkTime;
  /** Talk time summed over answered calls only — what the graphs plot. */
  answeredDurationSeconds: number;
  missedOrNotAnswered: number;
  busy: number;
  noAnswer: number;
  totalDurationSeconds: number;
  averageDurationSeconds: number;
  recordings: number;
  uniqueUsers: number;
}

export interface ReportSeriesPoint {
  label: string;
  total: number;
  inbound: number;
  outbound: number;
  answered: number;
  answeredByTalkTime: AnsweredTalkTime;
  answeredDurationSeconds: number;
  missedOrNotAnswered: number;
  busy: number;
  noAnswer: number;
  totalDurationSeconds: number;
  averageDurationSeconds: number;
}

export interface CallReportRow {
  id: ID;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  direction: CallDirection;
  status: CallStatus;
  fromNumber: string;
  toNumber: string;
  agentId: ID | null;
  agentName: string | null;
  leadName: string | null;
  campaignName: string | null;
  queueName: string | null;
  ringGroupName: string | null;
  durationSeconds: number;
  disposition: string | null;
  /** Latest AI call-analysis summary, when one has been produced. */
  summary: string | null;
  recordingSid: string | null;
  hasRecording: boolean;
  costCents: number | null;
}

export interface PaginatedReport<T> {
  total: number;
  items: T[];
}

export interface UserStatisticRow {
  userId: ID | null;
  user: string;
  numberOfCalls: number;
  answered: number;
  answeredByTalkTime: AnsweredTalkTime;
  missedOrNotAnswered: number;
  totalDurationSeconds: number;
  averageDurationSeconds: number;
  inbound: number;
  outbound: number;
}

export interface EntityStatisticRow {
  id: ID | null;
  name: string;
  numberOfCalls: number;
  answered: number;
  answeredByTalkTime: AnsweredTalkTime;
  missedOrNotAnswered: number;
  busy: number;
  noAnswer: number;
  totalDurationSeconds: number;
  averageDurationSeconds: number;
  users: UserStatisticRow[];
}

export interface MonthlyCallVolumePoint {
  month: string;
  answered: number;
  busy: number;
  noAnswer: number;
}

export interface MonthlyCallVolumeReport {
  inbound: MonthlyCallVolumePoint[];
  outbound: MonthlyCallVolumePoint[];
}

export interface UserStatisticsReport {
  summary: ReportSummary;
  users: UserStatisticRow[];
  daily: ReportSeriesPoint[];
  hourly: ReportSeriesPoint[];
}

export interface EntityStatisticsReport {
  summary: ReportSummary;
  entities: EntityStatisticRow[];
  distribution: ReportSeriesPoint[];
  answerTimeDistribution: ReportSeriesPoint[];
  users: UserStatisticRow[];
}

export interface OverviewReport {
  summary: ReportSummary;
  daily: ReportSeriesPoint[];
  hourly: ReportSeriesPoint[];
  users: UserStatisticRow[];
  monthlyVolume: MonthlyCallVolumeReport;
}

export interface ReportListOption {
  id: ID;
  name: string;
}

export interface DailyReportingSettings {
  id: ID;
  report: ReportKind;
  enabled: boolean;
  recipients: string[];
  formats: ReportFormat[];
  delivery: 'email' | 'ftp';
  ftpHost: string | null;
  ftpUsername: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReportEmailRequest {
  report: ReportKind;
  formats: ReportFormat[];
  filters: Partial<ReportFilters>;
  recipients?: string[];
}
